/**
 * Pure H OS · Inventory · GRN editor (create + edit draft + post + cancel).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, X, Send, XCircle, Save } from "lucide-react";
import { inventory, rawMaterials, InvGRN, InvGRNLine, InvWarehouse, RawMaterial } from "../../services/api";

const VENTURE = "orvan";
const CURRENCIES = ["EGP", "USD", "EUR", "SAR", "CNY"];
const UNITS = ["g", "kg", "ml", "L", "each", "drop"];

const emptyLine: InvGRNLine = {
  kind: "raw_material",
  itemRefId: "",
  lotLabel: "",
  quantity: 0,
  unit: "g",
  unitCostAmount: 0,
  unitCostCurrency: "EGP",
};

export default function GRNEditor() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const isNew = !id || id === "new";

  const [warehouses, setWarehouses] = useState<InvWarehouse[]>([]);
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [grn, setGrn] = useState<InvGRN | null>(null);
  const [warehouseId, setWarehouseId] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierRef, setSupplierRef] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<InvGRNLine[]>([{ ...emptyLine }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readOnly = !isNew && grn?.status !== "draft";

  const load = useCallback(async () => {
    const [whs, rms] = await Promise.all([
      inventory.listWarehouses(VENTURE),
      rawMaterials.list(VENTURE),
    ]);
    setWarehouses(whs);
    setMaterials(rms);
    if (!isNew && id) {
      const g = await inventory.getGRN(VENTURE, id);
      setGrn(g);
      setWarehouseId(g.warehouseId);
      setSupplierName(g.supplierName);
      setSupplierRef(g.supplierRef || "");
      setInvoiceDate(g.invoiceDate || "");
      setNotes(g.notes || "");
      setLines(g.lines.length ? g.lines : [{ ...emptyLine }]);
    } else if (whs.length && !warehouseId) {
      setWarehouseId(whs[0].id);
    }
  }, [id, isNew, warehouseId]);

  useEffect(() => { load(); }, [load]);

  function updateLine(idx: number, patch: Partial<InvGRNLine>) {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }
  function addLine() { setLines((ls) => [...ls, { ...emptyLine }]); }
  function removeLine(idx: number) { setLines((ls) => ls.filter((_, i) => i !== idx)); }

  async function saveDraft() {
    setBusy(true); setError(null);
    try {
      if (isNew) {
        const created = await inventory.createGRN(VENTURE, {
          warehouseId, supplierName,
          supplierRef: supplierRef || undefined,
          invoiceDate: invoiceDate || undefined,
          notes: notes || undefined,
          lines,
        });
        nav(`/pureh/inventory/grn/${created.id}`);
      } else if (id) {
        await inventory.patchGRN(VENTURE, id, {
          supplierName, supplierRef: supplierRef || undefined,
          invoiceDate: invoiceDate || undefined, notes: notes || undefined, lines,
        });
        await load();
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function postDraft() {
    if (!id || isNew) return;
    if (!confirm("Post this GRN? Stock items and movements will be created. This action cannot be edited afterwards.")) return;
    setBusy(true); setError(null);
    try {
      const res = await inventory.postGRN(VENTURE, id);
      setGrn(res.grn);
      alert(`Posted: ${res.movements.length} movement(s), ${res.stockItems.length} stock item(s) created.`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function cancelDraft() {
    if (!id || isNew) return;
    if (!confirm("Cancel this draft GRN?")) return;
    try {
      await inventory.cancelGRN(VENTURE, id);
      await load();
    } catch (e) {
      alert((e as Error).message);
    }
  }

  return (
    <section data-testid="grn-editor">
      <Link to="/pureh/inventory/grn" className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3" data-testid="grn-back-link">
        <ArrowLeft size={12} /> All GRNs
      </Link>

      <div className="flex items-center justify-between mb-4">
        <div>
          {grn ? (
            <>
              <div className="text-[11px] font-mono text-slate-400" data-testid="grn-doc-number">{grn.docNumber}</div>
              <h2 className="text-xl font-serif" style={{ color: "var(--ph-ink-strong)" }}>
                {grn.supplierName}
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium align-middle"
                  data-testid="grn-status-badge"
                  style={{
                    background: grn.status === "posted" ? "#dcfce7" : grn.status === "cancelled" ? "#f1f5f9" : "#e0f2fe",
                    color: grn.status === "posted" ? "#15803d" : grn.status === "cancelled" ? "#64748b" : "#075985",
                  }}>
                  {grn.status}
                </span>
              </h2>
            </>
          ) : (
            <h2 className="text-xl font-serif" style={{ color: "var(--ph-ink-strong)" }}>New GRN</h2>
          )}
        </div>
        <div className="flex gap-2">
          {!readOnly && (
            <button
              data-testid="grn-save-btn"
              onClick={saveDraft}
              disabled={busy || !warehouseId || !supplierName.trim() || lines.length === 0}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium"
              style={{ background: "var(--ph-surface)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-sm)" }}
            >
              <Save size={12} /> {isNew ? "Save draft" : "Save"}
            </button>
          )}
          {grn?.status === "draft" && (
            <>
              <button
                data-testid="grn-post-btn"
                onClick={postDraft}
                disabled={busy || lines.length === 0}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white"
                style={{ background: "var(--ph-brand-primary)", borderRadius: "var(--ph-radius-sm)" }}
              >
                <Send size={12} /> Post
              </button>
              <button
                data-testid="grn-cancel-btn"
                onClick={cancelDraft}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600"
                style={{ background: "var(--ph-surface)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-sm)" }}
              >
                <XCircle size={12} /> Cancel draft
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div data-testid="grn-editor-error" className="text-xs mb-3 p-2 rounded" style={{ background: "#fef2f2", color: "#b91c1c" }}>
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Panel title="Destination">
          <FormRow label="Warehouse">
            <select data-testid="grn-warehouse" disabled={readOnly || !isNew} className="w-full px-2 py-1.5 text-sm border rounded" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              <option value="">Select…</option>
              {warehouses.filter((w) => w.active).map((w) => (
                <option key={w.id} value={w.id}>{w.name} ({w.type})</option>
              ))}
            </select>
          </FormRow>
        </Panel>
        <Panel title="Supplier">
          <FormRow label="Supplier name">
            <input data-testid="grn-supplier-name" disabled={readOnly} className="w-full px-2 py-1.5 text-sm border rounded" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} />
          </FormRow>
          <FormRow label="PO / invoice reference">
            <input data-testid="grn-supplier-ref" disabled={readOnly} className="w-full px-2 py-1.5 text-sm border rounded" value={supplierRef} onChange={(e) => setSupplierRef(e.target.value)} />
          </FormRow>
          <FormRow label="Invoice date">
            <input data-testid="grn-invoice-date" type="date" disabled={readOnly} className="w-full px-2 py-1.5 text-sm border rounded" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
          </FormRow>
          <FormRow label="Notes">
            <textarea data-testid="grn-notes" disabled={readOnly} className="w-full px-2 py-1.5 text-sm border rounded" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </FormRow>
        </Panel>
      </div>

      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>Lines · {lines.length}</h3>
        {!readOnly && (
          <button data-testid="grn-add-line-btn" onClick={addLine} className="inline-flex items-center gap-1 px-2 py-1 text-xs" style={{ background: "var(--ph-surface)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-sm)" }}>
            <Plus size={11} /> Add line
          </button>
        )}
      </div>

      <div className="space-y-2">
        {lines.map((line, idx) => (
          <div key={idx} data-testid={`grn-line-${idx}`} className="grid grid-cols-12 gap-2 items-center p-2" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-sm)" }}>
            <div className="col-span-4">
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-0.5">Raw material</div>
              <select data-testid={`grn-line-${idx}-item`} disabled={readOnly} className="w-full px-2 py-1 text-xs border rounded" value={line.itemRefId} onChange={(e) => updateLine(idx, { itemRefId: e.target.value })}>
                <option value="">Select…</option>
                {materials.map((m) => (
                  <option key={m.id} value={m.id}>{(m as any).docNumber || m.code} · {m.name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-0.5">Lot</div>
              <input data-testid={`grn-line-${idx}-lot`} disabled={readOnly} className="w-full px-2 py-1 text-xs border rounded" value={line.lotLabel} onChange={(e) => updateLine(idx, { lotLabel: e.target.value })} />
            </div>
            <div className="col-span-1">
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-0.5">Qty</div>
              <input data-testid={`grn-line-${idx}-qty`} disabled={readOnly} type="number" className="w-full px-2 py-1 text-xs border rounded" value={line.quantity} onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) })} />
            </div>
            <div className="col-span-1">
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-0.5">Unit</div>
              <select data-testid={`grn-line-${idx}-unit`} disabled={readOnly} className="w-full px-2 py-1 text-xs border rounded" value={line.unit} onChange={(e) => updateLine(idx, { unit: e.target.value })}>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="col-span-1">
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-0.5">Cost</div>
              <input data-testid={`grn-line-${idx}-cost`} disabled={readOnly} type="number" className="w-full px-2 py-1 text-xs border rounded" value={line.unitCostAmount} onChange={(e) => updateLine(idx, { unitCostAmount: Number(e.target.value) })} />
            </div>
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-0.5">Currency</div>
              <select data-testid={`grn-line-${idx}-currency`} disabled={readOnly} className="w-full px-2 py-1 text-xs border rounded" value={line.unitCostCurrency} onChange={(e) => updateLine(idx, { unitCostCurrency: e.target.value })}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-span-1 text-right">
              {!readOnly && lines.length > 1 && (
                <button data-testid={`grn-line-${idx}-remove`} onClick={() => removeLine(idx)} className="text-slate-400 hover:text-red-600">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {grn?.status === "posted" && (
        <div className="mt-6 p-3 text-xs" data-testid="grn-posted-note" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "var(--ph-radius-sm)", color: "#166534" }}>
          Posted at {grn.postedAt && new Date(grn.postedAt).toLocaleString()} by {grn.postedBy}.
          Stock items and receive movements have been created. Any changes now require a stock adjustment.
        </div>
      )}
    </section>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="p-4" style={{ background: "var(--ph-surface-card)", border: "1px solid var(--ph-border-subtle)", borderRadius: "var(--ph-radius-md)" }}>
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
